import { useCallback, useState } from "react";

import { apiPatchLocation } from "@/features/profile/api/profile.api";

const WEB_LOCATION_PERMISSION_HINT_KEY = "zs_web_location_permission_granted";

const rememberLocationPermissionHint = () => {
  try {
    localStorage.setItem(WEB_LOCATION_PERMISSION_HINT_KEY, "1");
  } catch {
    // Ignore storage issues.
  }
};

const clearLocationPermissionHint = () => {
  try {
    localStorage.removeItem(WEB_LOCATION_PERMISSION_HINT_KEY);
  } catch {
    // Ignore storage issues.
  }
};

export type LocationSyncResult = {
  success: boolean;
  coords?: { latitude: number; longitude: number };
  errorType?: "PERMISSION_DENIED" | "TIMEOUT" | "POSITION_UNAVAILABLE" | "NETWORK_ERROR" | "NOT_SUPPORTED";
};

const getPositionAsync = ({
  highAccuracy,
  timeout,
  maximumAge,
}: {
  highAccuracy: boolean;
  timeout: number;
  maximumAge: number;
}): Promise<GeolocationPosition> =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      const err = new Error("Geolocation non supportee") as any;
      err.code = 0;
      reject(err);
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      timeout,
      maximumAge,
      enableHighAccuracy: highAccuracy,
    });
  });

const getPosition = async (): Promise<GeolocationPosition> => {
  try {
    return await getPositionAsync({
      highAccuracy: false,
      timeout: 10000,
      maximumAge: 300000,
    });
  } catch (err: any) {
    if (err?.code === 2 || err?.code === 3) {
      await new Promise((r) => setTimeout(r, 1500));
      return await getPositionAsync({
        highAccuracy: true,
        timeout: 20000,
        maximumAge: 0,
      });
    }
    throw err;
  }
};

export const useWebLocationSync = () => {
  const [isSyncing, setIsSyncing] = useState(false);

  const syncLocation = useCallback(async (): Promise<LocationSyncResult> => {
    setIsSyncing(true);
    try {
      const position = await getPosition();
      const coords = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      };
      await apiPatchLocation(coords);
      rememberLocationPermissionHint();
      return { success: true, coords };
    } catch (error: any) {
      let errorType: LocationSyncResult["errorType"] = "POSITION_UNAVAILABLE";
      if (error?.code === 0 || !navigator.geolocation) {
        errorType = "NOT_SUPPORTED";
      } else if (error?.code === 1) {
        errorType = "PERMISSION_DENIED";
      } else if (error?.code === 3) {
        errorType = "TIMEOUT";
      } else if (error?.code === 2) {
        errorType = "POSITION_UNAVAILABLE";
      } else if (error?.message?.includes("network") || error?.message?.includes("Network")) {
        errorType = "NETWORK_ERROR";
      }

      if (errorType === "PERMISSION_DENIED" || errorType === "NOT_SUPPORTED") {
        clearLocationPermissionHint();
      }

      return { success: false, errorType };
    } finally {
      setIsSyncing(false);
    }
  }, []);

  return { syncLocation, isSyncing };
};
